import { useContext, useMemo } from 'react';
import { Group } from '@visx/group';
import { DataContext } from '@visx/xychart';

import { SERIES_AREA_STACK_MARKER_RADIUS_PX } from '../../constants';
import type { AreaStackSeriesConfig } from '../../types/AreaStackSeriesProps';
import type { ChartDataPoint } from '../../types/ChartDataPoint';
import type { XScaleType } from '../../types/Scale';
import type { XDataKey } from '../../types/XDataKey';
import { useTooltipContext } from '../Tooltip/TooltipContext';

import styles from './AreaStackSeries.module.css';

interface StackLineMarkersProps {
  /** Series configs (in stacking order) */
  series: AreaStackSeriesConfig[];
  /** Pre-processed data (null handling already applied) */
  data: ChartDataPoint[];
  /** Key used for x-axis values */
  xDataKey: XDataKey;
  /** Scale type of the x-axis */
  xScaleType: XScaleType;
  /** Color scale function */
  colorScale: (key: string) => string;
}

/**
 * Renders a vertical "stack line" with circle markers at each stacked series
 * boundary for the currently hovered data point.
 *
 * - Reads `activeXValue` from the tooltip context to know which x position is hovered.
 * - Reads `xScale` / `yScale` from visx's `DataContext` to map data values to SVG coords.
 * - Computes cumulative positive and negative stacked Y positions per series.
 * - Renders a vertical connecting `<line>` between the lowest and highest boundary,
 *   plus a `<circle>` at each boundary coloured by series.
 */
export function StackLineMarkers({ series, data, xDataKey, xScaleType, colorScale }: StackLineMarkersProps) {
  const { activeXValue } = useTooltipContext();
  const dataContext = useContext(DataContext);

  // Compute the marker positions for the active data point.
  const markerData = useMemo(() => {
    if (activeXValue === null || !dataContext?.xScale || !dataContext?.yScale || series.length === 0) {
      return null;
    }

    // Find the data point matching the active x value
    const datum = data.find((d) => d[xDataKey] === activeXValue);
    if (!datum) {
      return null;
    }

    const xScale = dataContext.xScale as (v: string | number) => number | undefined;
    const yScale = dataContext.yScale as (v: number) => number | undefined;

    // Convert x value to SVG coordinate
    const xInput = xScaleType === 'band' ? String(activeXValue) : activeXValue;
    const svgX = xScale(xInput);
    if (typeof svgX !== 'number' || !Number.isFinite(svgX)) {
      return null;
    }

    // Compute cumulative stacked Y positions.
    // Positive values stack upward from 0, negative values stack downward from 0.
    let positiveCumulative = 0;
    let negativeCumulative = 0;

    interface MarkerInfo {
      svgY: number;
      color: string;
    }

    const markers: MarkerInfo[] = [];

    for (const config of series) {
      const raw = datum[config.dataKey];
      const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;

      let boundaryValue: number;
      if (value >= 0) {
        positiveCumulative += value;
        boundaryValue = positiveCumulative;
      } else {
        negativeCumulative += value;
        boundaryValue = negativeCumulative;
      }

      const svgY = yScale(boundaryValue);
      if (typeof svgY !== 'number' || !Number.isFinite(svgY)) {
        continue;
      }

      const color = config.color ?? colorScale(config.seriesKey);
      markers.push({ svgY, color });
    }

    if (markers.length === 0) {
      return null;
    }

    // Vertical line spans from lowest to highest SVG y (note: SVG y is inverted)
    const allY = markers.map((m) => m.svgY);
    const lineTop = Math.min(...allY);
    const lineBottom = Math.max(...allY);

    return { svgX, markers, lineTop, lineBottom };
  }, [activeXValue, dataContext, data, xDataKey, xScaleType, series, colorScale]);

  const isActive = markerData !== null;
  const groupClassName = `${styles.stackLineGroup} ${isActive ? styles.stackLineGroupActive : styles.stackLineGroupInactive}`;

  // Always render the group (for CSS transitions); hide via class when inactive.
  if (!markerData) {
    return <Group className={groupClassName} />;
  }

  const { svgX, markers, lineTop, lineBottom } = markerData;

  return (
    <Group className={groupClassName}>
      {/* Vertical connecting line */}
      <line x1={svgX} y1={lineTop} x2={svgX} y2={lineBottom} className={styles.connectingLine} />

      {/* Circle marker at each stacked boundary */}
      {markers.map((marker, index) => (
        <circle
          // eslint-disable-next-line react/no-array-index-key -- stable order from series config
          key={index}
          cx={svgX}
          cy={marker.svgY}
          r={SERIES_AREA_STACK_MARKER_RADIUS_PX}
          fill={marker.color}
          stroke="var(--lp-color-bg-ui-primary)"
          strokeWidth={2}
          className={styles.marker}
        />
      ))}
    </Group>
  );
}
